import React from 'react';
import {
  Col,
  Divider,
  Form,
  Row,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import DisplayText from '../../common/text/DisplayText';
import FileUpload from '../../common/files/FileUpload';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import { getExternalFormTextList } from '../formHelpers';

const validExternalLogoTypes = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml']);

const externalLogoValidator = (file) => {
  if (validExternalLogoTypes.has(file.type)) return true;
  message.error('External form logos must be PNG, JPEG, or SVG files');
  return false;
};

export default function ExternalFormBrandingSettings({
  baseEndpoint,
  externalFormURL,
  externalFormLogoPreview,
  externalFormLogoFileType,
  externalFormText,
  isDisplay,
  onExternalLogoAdd,
  onExternalLogoRemove,
}) {
  const textList = getExternalFormTextList(externalFormText);
  return (
    <>
      <Form.Item name="externalFormURL" label="External Form URL">
        <Typography.Paragraph className="external-form-branding-settings-text">
          {`${baseEndpoint}${externalFormURL}`}
        </Typography.Paragraph>
      </Form.Item>
      <Divider orientation="left">External Form Branding</Divider>
      <Row gutter={[24, 16]} align="top" className="external-form-branding-settings-row">
        <Col xs={24} md={8}>
          <div className="external-form-branding-settings-label">External Form Logo</div>
          <DisplayText title="Upload a custom logo for this external form. Removing it will use your company logo." />
          <DisplayText title="Supported file types: PNG, JPEG, SVG" />
        </Col>
        <Col xs={24} md={16}>
          {isDisplay ? (
            <>
              {externalFormLogoPreview && (
                <img
                  src={externalFormLogoPreview}
                  alt="External Form Logo"
                  className="external-form-branding-settings-logo-preview"
                />
              )}
              <Typography.Paragraph className="external-form-branding-settings-text">
                {externalFormLogoFileType ? 'Custom logo' : 'Default company logo'}
              </Typography.Paragraph>
            </>
          ) : (
            <>
              {externalFormLogoPreview ? (
                <Row gutter={10} align="middle" className="external-form-branding-settings-row">
                  <Col>
                    <img
                      src={externalFormLogoPreview}
                      alt="External Form Logo"
                      className="external-form-branding-settings-logo-preview"
                    />
                  </Col>
                  <Col>
                    <BorderlessButton
                      iconNode={<DeleteOutlined />}
                      onClick={onExternalLogoRemove}
                    />
                  </Col>
                </Row>
              ) : (
                <div className="external-form-branding-settings-logo-upload">
                  <FileUpload
                    addFile={onExternalLogoAdd}
                    customValidator={externalLogoValidator}
                  />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
      <Row gutter={[24, 16]} align="top" className="external-form-branding-settings-row">
        <Col xs={24} md={8}>
          <div className="external-form-branding-settings-label">External Form Text</div>
          <DisplayText title="Each text line appears under the logo. Leave a line empty to add vertical space." />
        </Col>
        <Col xs={24} md={16}>
          {isDisplay ? (
            <>
              {textList.map(({ key, value }) => (
                <Typography.Paragraph
                  key={key}
                  className="external-form-branding-settings-text external-form-branding-settings-list-text"
                >
                  {value === '' ? '\u00A0' : value}
                </Typography.Paragraph>
              ))}
            </>
          ) : (
            <Form.List name="externalFormText">
              {(textFields, { add, remove }) => (
                <>
                  {textFields.map((field, index) => (
                    <Row key={field.key} gutter={10} align="middle" className="external-form-branding-settings-row">
                      <Col>
                        <Form.Item
                          {...field}
                          className="external-form-branding-settings-form-item"
                        >
                          <OnTraccrTextInput
                            textarea
                            placeholder={`Text ${index + 1}`}
                            className="OnTraccrInput external-form-branding-settings-input"
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <BorderlessButton
                          iconNode={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <OnTraccrButton
                    title="Add Text"
                    onClick={() => add('')}
                    className="external-form-branding-settings-add-text"
                  />
                </>
              )}
            </Form.List>
          )}
        </Col>
      </Row>
    </>
  );
}

ExternalFormBrandingSettings.propTypes = {
  baseEndpoint: PropTypes.string,
  externalFormURL: PropTypes.string,
  externalFormLogoPreview: PropTypes.string,
  externalFormLogoFileType: PropTypes.string,
  externalFormText: PropTypes.arrayOf(PropTypes.string),
  isDisplay: PropTypes.bool,
  onExternalLogoAdd: PropTypes.func,
  onExternalLogoRemove: PropTypes.func,
};

ExternalFormBrandingSettings.defaultProps = {
  baseEndpoint: 'https://app.ontraccr.com/',
  externalFormURL: '',
  externalFormLogoPreview: null,
  externalFormLogoFileType: null,
  externalFormText: [],
  isDisplay: false,
  onExternalLogoAdd: () => {},
  onExternalLogoRemove: () => {},
};
